import React, { useEffect, useState } from 'react';
import { Check, X, AlertCircle, Info, AlertTriangle } from 'lucide-react';
export type ToastType = 'success' | 'error' | 'warning' | 'info';
export interface ToastProps {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  onClose: (id: string) => void;
  duration?: number;
}
export function Toast({
  id,
  type,
  title,
  message,
  onClose,
  duration = 4000
}: ToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    // Trigger enter animation
    requestAnimationFrame(() => setIsVisible(true));
    // Auto dismiss
    const timer = setTimeout(() => {
      handleClose();
    }, duration);
    return () => clearTimeout(timer);
  }, [duration]);
  const handleClose = () => {
    setIsVisible(false);
    // Wait for exit animation to finish before removing from DOM
    setTimeout(() => onClose(id), 300);
  };
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <Check className="w-[18px] h-[18px] text-[#10B981]" data-id="element-6322" data-mp="q7ejwagd0k9x" />;
      case 'error':
        return <AlertCircle className="w-[18px] h-[18px] text-[#EF4444]" data-id="element-6323" data-mp="l8l58epc6n86" />;
      case 'warning':
        return <AlertTriangle className="w-[18px] h-[18px] text-[#F59E0B]" data-id="element-6324" data-mp="z8cpgx6g4zum" />;
      case 'info':
        return <Info className="w-[18px] h-[18px] text-[#3B82F6]" data-id="element-6325" data-mp="z601o3e0bv9n" />;
    }
  };
  const getBorderColor = () => {
    switch (type) {
      case 'success':
        return 'border-l-[#10B981]';
      case 'error':
        return 'border-l-[#EF4444]';
      case 'warning':
        return 'border-l-[#F59E0B]';
      case 'info':
        return 'border-l-[#3B82F6]';
    }
  };
  return <div className={`
        fixed bottom-6 right-6 z-[1080] min-w-[320px] max-w-[420px]
        bg-white rounded-lg shadow-[0_4px_6px_rgba(0,0,0,0.1)]
        border border-[#E2E8F0] border-l-4 ${getBorderColor()}
        p-4 flex items-start gap-3
        transition-all duration-300 ease-in-out transform
        ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
      `} role="alert" data-id="element-6326" data-mp="1hyc74yzyjco0">
      <div className="flex-shrink-0 mt-0.5" data-id="element-6327" data-mp="kifxzmn98tc6">{getIcon()}</div>
      <div className="flex-1 min-w-0" data-id="element-6328" data-mp="on5sb729lzuz">
        <h4 className="text-[14px] font-semibold text-[#0F172A] leading-5" data-id="element-6329" data-mp="ok1j1v9y0dxg">
          {title}
        </h4>
        {message && <p className="text-[13px] font-normal text-[#64748B] mt-1 leading-5" data-id="element-6330" data-mp="ybxhx8jus82q">
            {message}
          </p>}
      </div>
      <button onClick={handleClose} className="flex-shrink-0 text-[#94A3B8] hover:text-[#0F172A] transition-colors p-1 rounded-md hover:bg-[#F1F5F9]" aria-label="Close notification" data-id="element-6331" data-mp="13btmb8wgq1it">
        <X className="w-[18px] h-[18px]" data-id="element-6332" data-mp="1cbyj6p415hiq" />
      </button>
    </div>;
}
// Toast Container to manage multiple toasts
export function ToastContainer() {
  // This would typically be connected to a global context
  // For this implementation, we'll export a hook or context separately if needed
  return null;
}